import React from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { getUserDisplayName, formatUserRole } from "../../../utils/avatarUtils";

const MemberTable = ({
  members,
  sortConfig,
  onSort,
  onEditRole,
  onViewActivity,
  onRemoveMember,
  onMemberClick,
  selectedMembers,
  onSelectMember,
  onSelectAll,
  canManageMembers,
  canRemoveMembers,
}) => {
  const getRoleColor = (role) => {
    if (!role) return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
    switch (role.toLowerCase()) {
      case "owner":
        return "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:border-purple-800";
      case "admin":
        return "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800";
      case "member":
        return "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700";
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "online":
      case "active":
        return "bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.5)]";
      case "away":
        return "bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.5)]";
      case "offline":
      case "inactive":
        return "bg-slate-400";
      case "pending":
        return "bg-indigo-500";
      default:
        return "bg-slate-400";
    }
  };

  const formatLastActivity = (date, status = 'active') => {
    if (!date) return "Never";
    const activityDate = new Date(date);
    if (isNaN(activityDate.getTime())) return "Never";

    const now = new Date();
    const diffInMs = now - activityDate;
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    const prefix = status === 'pending' ? 'Invited' : 'Active';

    if (diffInMinutes < 5 && status !== 'pending') return "Online now";
    if (diffInMinutes < 60) return `${prefix} ${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${prefix} ${diffInHours}h ago`;
    if (diffInDays < 7) return `${prefix} ${diffInDays}d ago`;
    
    return `${prefix} on ${activityDate.toLocaleDateString()}`;
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) {
      return (
        <Icon name="ArrowUpDown" size={16} className="text-text-secondary" />
      );
    }
    return sortConfig.direction === "asc" ? (
      <Icon name="ArrowUp" size={16} className="text-primary" />
    ) : (
      <Icon name="ArrowDown" size={16} className="text-primary" />
    );
  };

  const isAllSelected =
    members.length > 0 && selectedMembers.length === members.length;
  const isIndeterminate =
    selectedMembers.length > 0 && selectedMembers.length < members.length;

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-ambient">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = isIndeterminate;
                  }}
                  onChange={onSelectAll}
                  className="rounded border-border text-primary focus:ring-primary"
                />
              </th>
              <th
                className="text-left px-4 py-3 font-medium text-text-primary cursor-pointer hover:bg-muted/70 transition-colors"
                onClick={() => onSort("name")}
              >
                <div className="flex items-center space-x-2">
                  <span>Member</span>
                  {getSortIcon("name")}
                </div>
              </th>
              <th
                className="text-left px-4 py-3 font-medium text-text-primary cursor-pointer hover:bg-muted/70 transition-colors"
                onClick={() => onSort("role")}
              >
                <div className="flex items-center space-x-2">
                  <span>Role</span>
                  {getSortIcon("role")}
                </div>
              </th>
              <th
                className="text-left px-4 py-3 font-medium text-text-primary cursor-pointer hover:bg-muted/70 transition-colors"
                onClick={() => onSort("lastActivity")}
              >
                <div className="flex items-center space-x-2">
                  <span>Last Activity</span>
                  {getSortIcon("lastActivity")}
                </div>
              </th>
              <th
                className="text-left px-4 py-3 font-medium text-text-primary cursor-pointer hover:bg-muted/70 transition-colors"
                onClick={() => onSort("status")}
              >
                <div className="flex items-center space-x-2">
                  <span>Status</span>
                  {getSortIcon("status")}
                </div>
              </th>
              <th className="text-right px-4 py-3 font-medium text-text-primary">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {members.map((member) => (
              <tr
                key={member.id}
                className="hover:bg-muted/30 transition-colors"
              >
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    checked={selectedMembers.includes(member.id)}
                    onChange={() => onSelectMember(member.id)}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                </td>
                <td className="px-4 py-4">
                  <div 
                    className="flex items-center space-x-3 cursor-pointer group"
                    onClick={() => onMemberClick && onMemberClick(member)}
                  >
                    <div className="relative">
                      <div
                        className={`w-3 h-3 ${getStatusColor(
                          member.status
                        )} rounded-full border-2 border-white`}
                      ></div>
                    </div>
                    <div>
                      <div className="font-medium text-text-primary group-hover:text-primary transition-colors">
                        {getUserDisplayName(member)}
                      </div>
                      <div className="text-sm text-text-secondary">
                        {member.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleColor(
                      member.role
                    )}`}
                  >
                    {formatUserRole(member.role || 'member')}
                  </span>
                </td>
                <td className="px-4 py-4 text-sm text-text-secondary">
                  {formatLastActivity(member.lastActivity, member.status)}
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center space-x-2">
                    <div
                      className={`w-2 h-2 ${getStatusColor(
                        member.status
                      )} rounded-full`}
                    ></div>
                    <span className="text-sm text-text-secondary capitalize">
                      {member.status}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center justify-end space-x-1">
                    {canManageMembers && onEditRole && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onEditRole(member)}
                        className="h-8 w-8"
                        disabled={member.status === 'pending'}
                        title={member.status === 'pending' ? 'Cannot edit role for pending invitation' : 'Edit role'}
                      >
                        <Icon name="Edit" size={16} className={member.status === 'pending' ? 'opacity-50' : ''} />
                      </Button>
                    )}

                    {canRemoveMembers && onRemoveMember && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => onRemoveMember(member)}
                        className="h-8 w-8 text-destructive hover:text-destructive"
                      >
                        <Icon name="Trash2" size={16} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MemberTable;
